import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonBase, LinearProgress, Paper, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import moment from 'moment-timezone';
import mixpanel from 'mixpanel-browser';
import Page from '../../components/page/Page';
import BrandLoader from '../../components/BrandLoader';
import { useMeCore } from '../../../core/modules/me';
import { useCourseCore } from '../../../core/modules/course';
import { HomeCourse, useHomeCore } from '../../../core/modules/home';
import { useNavigationCore } from '../../../core/router/navigation';
import useNewNavigation from '../../../core/hooks/useNewNavigation';
import { getLastVideoPosition, LastVideoPosition } from '../../../core/utils/videoPosition';
import { Course } from '../../../core/interfaces/course.interface';
import { capitalizeFirstLetter } from '../../../core/utils/utils';
import { CLUB_DE_TRADING_COURSE_ID, MARATON_DE_INVERSIONES_COURSE_ID, getBrandedCourseTheme, getCourseTheme, isClubDeTradingCourse, isMaratonDeInversionesCourse } from '../../../core/constants/courses';
import useCourseTheme from '../../../core/hooks/useCourseTheme';

const ACCENT = '#4F46E5';
const BORDER = '1px solid #E2E8F0';
const MAX_LIVE_EVENTS = 3;
const MAX_REPORTS = 2;
// Máximo de cursos para los que el Inicio pide detalle (un admin ve todos los cursos).
const MAX_COURSES_WITH_DETAIL = 8;

// Último Inicio cargado: al volver se muestra al tiro y se refresca por detrás
let homeCache: { key: string; items: HomeCourse[] } | null = null;

interface Continue {
  item: HomeCourse;
  classId: string;
  className: string;
  moduleLabel: string;
  videoUrl?: string;
  kind: 'resume' | 'next' | 'start';
  position?: LastVideoPosition;
}

const formatTime = (totalSeconds: number) => {
  const s = Math.floor(totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = String(s % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
};

const courseProgress = (item: HomeCourse) => {
  const ids = item.modules.flatMap((module) => module.classes.map((mClass) => mClass.class.id));
  const done = ids.filter((id) => item.views.includes(id)).length;
  return { done, total: ids.length, percent: ids.length ? Math.round((done * 100) / ids.length) : 0 };
};

const findClass = (item: HomeCourse, classId: string) => {
  for (let m = 0; m < item.modules.length; m += 1) {
    const module = item.modules[m];
    const mClass = module.classes.find((c) => `${c.class.id}` === `${classId}`);
    if (mClass) {
      return { module, moduleIndex: m, mClass };
    }
  }
  return null;
};

/** Qué mostrar arriba: la última clase a medio ver; si no hay, la siguiente clase sin ver. */
const pickContinue = (items: HomeCourse[], userId?: string): Continue | null => {
  const position = userId ? getLastVideoPosition(userId) : null;
  if (position) {
    for (const item of items) {
      const found = findClass(item, position.classId);
      if (found) {
        return {
          item,
          classId: found.mClass.class.id,
          className: found.mClass.class.name,
          moduleLabel: `Módulo ${found.moduleIndex + 1} · ${found.module.name}`,
          videoUrl: found.mClass.class.videoLinks?.[0]?.url,
          kind: 'resume',
          position,
        };
      }
    }
  }
  for (const item of items) {
    for (let m = 0; m < item.modules.length; m += 1) {
      const module = item.modules[m];
      const mClass = module.classes.find((c) => !item.views.includes(c.class.id));
      if (mClass) {
        return {
          item,
          classId: mClass.class.id,
          className: mClass.class.name,
          moduleLabel: `Módulo ${m + 1} · ${module.name}`,
          videoUrl: mClass.class.videoLinks?.[0]?.url,
          kind: item.views.length ? 'next' : 'start',
        };
      }
    }
  }
  return null;
};

const courseAccent = (course: Course) => getBrandedCourseTheme(course)?.main ?? ACCENT;

const FREE_TRIAL_CLASS = /30\s*d[ií]as\s*gratis/i;

interface FreeTrial {
  item: HomeCourse;
  classId: string;
  club: boolean;
  videoUrl?: string;
}

/** Clase de instrucciones de los 30 días gratis de un curso grabado; el producto (Club o Membresía) sale del nombre. */
const findFreeTrial = (item: HomeCourse): FreeTrial | null => {
  for (const module of item.modules) {
    for (const mClass of module.classes) {
      const text = `${mClass.class.name} ${module.name}`;
      if (FREE_TRIAL_CLASS.test(text)) {
        return { item, classId: mClass.class.id, club: /club/i.test(text), videoUrl: mClass.class.videoLinks?.[0]?.url };
      }
    }
  }
  return null;
};

const Home = () => {
  // Con un curso elegido en el selector, el Inicio toma su color (Club azul, Maratón verde)
  const courseTheme = useCourseTheme();
  const accent = courseTheme?.main ?? ACCENT;
  const { user, studentCourses, profesorCourses, getMeCourses } = useMeCore();
  const { course: currentCourse, courses, getCourses, setCurrentClassId } = useCourseCore();
  const { getHomeCourses, getVideoThumbnail } = useHomeCore();
  const { goToMeCourse, goToCalendar, goToCourseReports } = useNavigationCore();
  const { hideNavbar } = useNewNavigation();

  const [items, setItems] = useState<HomeCourse[] | null>(null);
  const [failed, setFailed] = useState(false);
  // La lista de cursos guardada puede venir vacía (recién iniciada la sesión): "no tienes cursos" solo tras la respuesta de la API
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [trialThumbnail, setTrialThumbnail] = useState<string | null>(null);

  const loadCourses = useCallback(() => {
    if (!user) {
      return;
    }
    const onSuccess = () => setCoursesLoaded(true);
    const onFailure = () => setFailed(true);
    if (user.userRole.admin) {
      getCourses({}, onSuccess, onFailure);
    } else {
      getMeCourses(onSuccess, onFailure);
    }
  }, [getCourses, getMeCourses, user]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const userCourses: Course[] | null = useMemo(() => {
    if (!user) return null;
    if (user.userRole.admin) return courses;
    if (user.userRole.student) return studentCourses.map((studentCourse) => studentCourse.course);
    if (user.userRole.professor) return profesorCourses;
    return [];
  }, [courses, profesorCourses, studentCourses, user]);

  // Cursos con detalle: los primeros N y, si no está entre ellos, el curso elegido en el selector
  const detailCourses = useMemo(() => {
    if (!userCourses) return null;
    const list = userCourses.slice(0, MAX_COURSES_WITH_DETAIL);
    const selected = currentCourse && userCourses.find((c) => c.id === currentCourse.id);
    return selected && !list.includes(selected) ? [...list, selected] : list;
  }, [currentCourse, userCourses]);

  const courseKey = useMemo(() => `${user?.id}:${(detailCourses ?? []).map((course) => course.id).join(',')}`, [detailCourses, user?.id]);

  const loadHome = useCallback(() => {
    if (!detailCourses) {
      return;
    }
    if (!detailCourses.length) {
      return;
    }
    if (homeCache?.key === courseKey) {
      setItems(homeCache.items);
    }
    setFailed(false);
    getHomeCourses(
      detailCourses,
      (data) => {
        homeCache = { key: courseKey, items: data };
        setItems(data);
      },
      () => setFailed(true),
    );
    // courseKey evita volver a pedir todo cuando el arreglo cambia de referencia pero no de cursos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseKey, getHomeCourses]);

  useEffect(() => {
    loadHome();
  }, [loadHome]);

  useEffect(() => {
    if (coursesLoaded && detailCourses && !detailCourses.length) {
      setItems([]);
    }
  }, [coursesLoaded, detailCourses]);

  const retry = () => {
    setFailed(false);
    if (coursesLoaded) {
      loadHome();
    } else {
      loadCourses();
    }
  };

  // Una vez por visita (con el Inicio guardado, items llega dos veces: el guardado y el fresco)
  const openedTracked = useRef(false);
  useEffect(() => {
    if (items && !openedTracked.current && process.env.REACT_APP_MIXPANEL_TOKEN) {
      openedTracked.current = true;
      mixpanel.track('Home opened', { courses: items.length });
    }
  }, [items]);

  // El Inicio se enfoca en el curso elegido en el selector (si hay uno); si no, junta todos
  const focusItems = useMemo(() => {
    if (!items) return null;
    const focused = currentCourse ? items.find((item) => item.course.id === currentCourse.id) : undefined;
    return focused ? [focused] : items;
  }, [currentCourse, items]);
  const focusedCourse = focusItems && focusItems.length === 1 && (items?.length ?? 0) > 1 ? focusItems[0].course : null;

  const continueItem = useMemo(() => (focusItems ? pickContinue(focusItems, user?.id) : null), [focusItems, user?.id]);

  useEffect(() => {
    setThumbnail(null);
    if (continueItem?.videoUrl) {
      getVideoThumbnail(continueItem.videoUrl).then(setThumbnail);
    }
  }, [continueItem?.videoUrl, getVideoThumbnail]);

  // Acceso a los 30 días gratis del curso grabado; no se muestra a quien ya está en ESE producto (un socio del Club sí ve la de la Membresía)
  const freeTrial = useMemo(() => {
    if (!focusItems || !userCourses) return null;
    for (const item of focusItems) {
      const trial = findFreeTrial(item);
      const owned = !user?.userRole.admin && userCourses.some((course) => (trial?.club ? isClubDeTradingCourse(course) : isMaratonDeInversionesCourse(course)));
      if (trial && !owned) {
        return trial;
      }
    }
    return null;
  }, [focusItems, user?.userRole.admin, userCourses]);

  // La tarjeta de los 30 días gratis va en el color del producto que ofrece (Club azul, Membresía verde)
  const trialTheme = getCourseTheme({ id: freeTrial?.club ? CLUB_DE_TRADING_COURSE_ID : MARATON_DE_INVERSIONES_COURSE_ID });

  useEffect(() => {
    setTrialThumbnail(null);
    if (freeTrial?.videoUrl) {
      getVideoThumbnail(freeTrial.videoUrl).then(setTrialThumbnail);
    }
  }, [freeTrial?.videoUrl, getVideoThumbnail]);

  const liveEvents = useMemo(() => {
    if (!focusItems) return [];
    const from = moment().subtract(2, 'hours');
    return focusItems
      .flatMap((item) => item.events.map((event) => ({ event, course: item.course })))
      .filter(({ event }) => event.presential && moment(event.datetime).isAfter(from))
      .sort((a, b) => moment(a.event.datetime).valueOf() - moment(b.event.datetime).valueOf())
      .slice(0, MAX_LIVE_EVENTS);
  }, [focusItems]);

  const reports = useMemo(() => {
    if (!focusItems) return [];
    return focusItems
      .filter((item) => item.report)
      .sort((a, b) => moment(b.report!.createdAt).valueOf() - moment(a.report!.createdAt).valueOf())
      .slice(0, MAX_REPORTS);
  }, [focusItems]);

  const track = useCallback((action: string, course?: Course) => {
    if (process.env.REACT_APP_MIXPANEL_TOKEN) {
      mixpanel.track('Home action', { action, courseId: course?.id, courseName: course?.name });
    }
  }, []);

  const openClass = useCallback((cont: Continue) => {
    track('continue', cont.item.course);
    setCurrentClassId(cont.classId);
    goToMeCourse(cont.item.course.id, cont.classId);
  }, [goToMeCourse, setCurrentClassId, track]);

  const openCourse = useCallback((course: Course) => {
    track('course', course);
    setCurrentClassId('');
    goToMeCourse(course.id);
  }, [goToMeCourse, setCurrentClassId, track]);

  const openFreeTrial = useCallback((trial: FreeTrial) => {
    track('free_trial', trial.item.course);
    setCurrentClassId(trial.classId);
    goToMeCourse(trial.item.course.id, trial.classId);
  }, [goToMeCourse, setCurrentClassId, track]);

  const multipleCourses = (focusItems?.length ?? 0) > 1;

  // Cursos grabados (sin calendario) o sin sesiones próximas: en vez de "Próximas en vivo" se muestra el avance
  const progressItem = focusItems && focusItems.length === 1 ? focusItems[0] : continueItem?.item;
  const showProgress = Boolean(progressItem && (!progressItem.course.calendar || !liveEvents.length));
  const progressInfo = useMemo(() => {
    if (!progressItem) return null;
    const { done, total, percent } = courseProgress(progressItem);
    const modulesTotal = progressItem.modules.filter((module) => module.classes.length).length;
    const modulesDone = progressItem.modules.filter(
      (module) => module.classes.length && module.classes.every((mClass) => progressItem.views.includes(mClass.class.id)),
    ).length;
    const upNext: Continue[] = [];
    progressItem.modules.forEach((module, m) => {
      module.classes.forEach((mClass) => {
        if (upNext.length < 3 && !progressItem.views.includes(mClass.class.id) && mClass.class.id !== continueItem?.classId) {
          upNext.push({
            item: progressItem,
            classId: mClass.class.id,
            className: mClass.class.name,
            moduleLabel: `Módulo ${m + 1} · ${module.name}`,
            kind: 'next',
          });
        }
      });
    });
    return { done, total, percent, modulesDone, modulesTotal, upNext };
  }, [continueItem?.classId, progressItem]);
  const firstName = user?.name?.split(' ')[0] ?? '';

  const subtitle = useMemo(() => {
    const parts: string[] = [];
    if (continueItem?.kind === 'resume') parts.push('Tienes una clase a medio ver');
    const next = liveEvents[0];
    if (next && moment(next.event.datetime).diff(moment(), 'days') < 7) {
      parts.push(`${parts.length ? 'y una' : 'Tienes una'} sesión en vivo el ${moment(next.event.datetime).format('dddd')}`);
    }
    return parts.length ? `${parts.join(' ')}.` : 'Esto es lo que tienes para hoy.';
  }, [continueItem?.kind, liveEvents]);

  const content = (() => {
    if (failed && !items?.length) {
      return (
        <Box className="flex flex-col items-center justify-center py-96 gap-16 text-center">
          <Typography className="text-18 font-semibold">No pudimos cargar tu inicio</Typography>
          <Button variant="contained" color="secondary" onClick={retry}>
            Reintentar
          </Button>
        </Box>
      );
    }
    if (!items) {
      return <BrandLoader message="Preparando tu inicio..." />;
    }
    if (!items.length) {
      return (
        <Box className="flex flex-col items-center justify-center py-96 text-center px-24">
          <Typography className="text-20 font-bold">Aún no tienes cursos</Typography>
          <Typography color="text.secondary" className="mt-8">Cuando te inscribas en un curso aparecerá aquí.</Typography>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.55fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          {continueItem ? (
            <Paper
              elevation={0}
              sx={{ border: BORDER, borderRadius: 3, overflow: 'hidden', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' } }}
            >
              <ButtonBase onClick={() => openClass(continueItem)} sx={{ position: 'relative', minHeight: { xs: 190, md: 280 }, background: '#0F172A' }}>
                <Box
                  component="img"
                  src={thumbnail || continueItem.item.course.avatar}
                  alt=""
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: thumbnail ? 1 : 0.85 }}
                />
                <Box sx={{ position: 'relative', width: 64, height: 64, borderRadius: '50%', background: 'rgba(15,23,42,0.6)', display: 'grid', placeItems: 'center' }}>
                  <PlayArrowRoundedIcon sx={{ color: '#fff', fontSize: 40 }} />
                </Box>
              </ButtonBase>
              <Box sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={{ color: accent, fontWeight: 700, fontSize: 13.5, mb: 1 }}>
                  {continueItem.kind === 'resume' && continueItem.position
                    ? `Sigue donde quedaste · ${formatTime(continueItem.position.seconds)}${continueItem.position.duration ? ` de ${formatTime(continueItem.position.duration)}` : ''}`
                    : continueItem.kind === 'next' ? 'Tu siguiente clase' : 'Empieza aquí'}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 19, md: 22 }, lineHeight: 1.2, mb: 1 }}>
                  {continueItem.className}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
                  {continueItem.item.course.name} · {continueItem.moduleLabel}
                </Typography>
                {continueItem.position?.duration ? (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (continueItem.position.seconds * 100) / continueItem.position.duration)}
                    sx={{ mt: 2, height: 6, borderRadius: 3, backgroundColor: '#E8ECF1', '& .MuiLinearProgress-bar': { backgroundColor: accent } }}
                  />
                ) : null}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => openClass(continueItem)}
                  sx={{ mt: 2.5, alignSelf: { xs: 'stretch', md: 'flex-start' }, borderRadius: 2, px: 3, py: 1.25, fontWeight: 700 }}
                >
                  {continueItem.kind === 'resume' ? 'Continuar clase' : 'Ver clase'}
                </Button>
              </Box>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ border: BORDER, borderRadius: 3, p: 3 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 20 }}>¡Estás al día!</Typography>
              <Typography color="text.secondary" className="mt-8">Viste todas las clases de tus cursos.</Typography>
            </Paper>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
            {freeTrial ? (
              <Paper
                elevation={0}
                sx={{ border: `1px solid ${trialTheme.tintBorder}`, background: trialTheme.tint, borderRadius: 3, p: { xs: 2, md: 2.5 }, display: 'flex', gap: { xs: 1.75, md: 2 }, alignItems: { xs: 'flex-start', md: 'center' } }}
              >
                <ButtonBase
                  aria-label="Ver video de instrucciones"
                  onClick={() => openFreeTrial(freeTrial)}
                  sx={{ position: 'relative', width: { xs: 84, md: 112 }, height: { xs: 56, md: 72 }, flexShrink: 0, borderRadius: 2, overflow: 'hidden', background: '#1E293B' }}
                >
                  {trialThumbnail ? (
                    <Box component="img" src={trialThumbnail} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : null}
                  <Box sx={{ position: 'relative', width: { xs: 30, md: 36 }, height: { xs: 30, md: 36 }, borderRadius: '50%', background: 'rgba(15,23,42,0.6)', display: 'grid', placeItems: 'center' }}>
                    <PlayArrowRoundedIcon sx={{ color: '#fff', fontSize: { xs: 20, md: 24 } }} />
                  </Box>
                </ButtonBase>
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontSize: { xs: 11, md: 12 }, fontWeight: 700, color: trialTheme.dark, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {multipleCourses ? `Incluido con ${freeTrial.item.course.name}` : 'Incluido con tu curso'}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: 16, md: 17 }, lineHeight: 1.25, mt: 0.5 }}>
                    {freeTrial.club ? '30 días gratis en el Club de Trading' : '30 días gratis en la Membresía Maratón'}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#475569', mt: 0.5 }}>Mira el video y activa tu acceso en 2 minutos.</Typography>
                  <Button
                    variant="contained"
                    onClick={() => openFreeTrial(freeTrial)}
                    sx={{ mt: 1.5, alignSelf: 'flex-start', borderRadius: 999, px: 2.25, minHeight: 40, fontWeight: 700, color: '#fff', background: trialTheme.main, '&:hover': { background: trialTheme.dark, color: '#fff' } }}
                  >
                    Ver cómo activarlo
                  </Button>
                </Box>
              </Paper>
            ) : null}

            {showProgress && progressInfo ? (
              <Paper elevation={0} sx={{ border: BORDER, borderRadius: 3, p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Tu avance</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: 16, color: accent }}>{progressInfo.percent}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressInfo.percent}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: '#E8ECF1', '& .MuiLinearProgress-bar': { backgroundColor: accent } }}
                />
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1 }}>
                  {progressInfo.modulesDone} de {progressInfo.modulesTotal} módulos terminados · {progressInfo.done} de {progressInfo.total} clases
                </Typography>
                {progressInfo.upNext.length ? (
                  <>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 2, mb: 0.5 }}>Lo que sigue</Typography>
                    {progressInfo.upNext.map((next, index) => (
                      <ButtonBase
                        key={next.classId}
                        onClick={() => openClass(next)}
                        sx={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left', gap: 1.25, py: 1, borderTop: index ? BORDER : 'none' }}
                      >
                        <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#CBD5E1', flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{next.className}</Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{next.moduleLabel}</Typography>
                        </Box>
                      </ButtonBase>
                    ))}
                  </>
                ) : (
                  <Typography sx={{ fontSize: 14, mt: 2 }}>¡Terminaste todas las clases de este curso!</Typography>
                )}
              </Paper>
            ) : (
              <Paper elevation={0} sx={{ border: BORDER, borderRadius: 3, p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Próximas en vivo</Typography>
                  {liveEvents[0] ? (
                    <ButtonBase onClick={() => { track('calendar', liveEvents[0].course); goToCalendar(liveEvents[0].course.id); }} sx={{ color: accent, fontWeight: 700, fontSize: 13.5 }}>
                      Calendario
                    </ButtonBase>
                  ) : null}
                </Box>
                {liveEvents.length ? liveEvents.map(({ event, course }, index) => {
                  const date = moment(event.datetime);
                  return (
                    <Box key={`${course.id}-${event.id}`} display="flex" alignItems="center" gap={1.75} sx={{ py: 1.25, borderTop: index ? BORDER : 'none' }}>
                      <Box sx={{ width: 52, flexShrink: 0, textAlign: 'center', border: BORDER, borderRadius: 2, py: 0.5, lineHeight: 1.1 }}>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{capitalizeFirstLetter(date.format('ddd').replace('.', ''))}</Typography>
                        <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.1 }}>{date.format('DD')}</Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{date.format('MMM').replace('.', '')}</Typography>
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{event.title}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                          {date.format('HH:mm')} hrs{multipleCourses ? ` · ${course.name}` : ' · en vivo'}
                        </Typography>
                      </Box>
                      {event.link ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          endIcon={<LaunchIcon fontSize="small" />}
                          onClick={() => { track('join', course); window.open(event.link, '_blank'); }}
                          sx={{ borderRadius: 999, flexShrink: 0 }}
                        >
                          Unirme
                        </Button>
                      ) : null}
                    </Box>
                  );
                }) : (
                  <Typography color="text.secondary" sx={{ fontSize: 14 }}>No hay sesiones en vivo programadas.</Typography>
                )}
              </Paper>
            )}

            {reports.length ? (
              <Paper elevation={0} sx={{ border: BORDER, borderRadius: 3, p: 2.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1.5 }}>{reports.length > 1 ? 'Últimos reportes' : 'Último reporte'}</Typography>
                {reports.map((item, index) => (
                  <Box key={item.course.id} display="flex" alignItems="center" gap={1.75} sx={{ py: 1, borderTop: index ? BORDER : 'none' }}>
                    <Box sx={{ width: 44, height: 52, flexShrink: 0, borderRadius: 1.5, background: courseTheme?.tint ?? '#EEF2FF', color: accent, display: 'grid', placeItems: 'center' }}>
                      <PictureAsPdfOutlinedIcon />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>{item.report!.title}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        {moment(item.report!.createdAt).format('DD/MM/YYYY')}{multipleCourses ? ` · ${item.course.name}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => { track('report', item.course); goToCourseReports(item.course.id); }}
                      sx={{ borderRadius: 2, flexShrink: 0, color: '#0F172A', borderColor: '#CBD5E1' }}
                    >
                      Abrir
                    </Button>
                  </Box>
                ))}
              </Paper>
            ) : null}
          </Box>
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: 18, mt: 4, mb: 1.5 }}>Tus cursos</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {items.map((item) => {
            const progress = courseProgress(item);
            const itemAccent = courseAccent(item.course);
            return (
              <ButtonBase
                key={item.course.id}
                onClick={() => openCourse(item.course)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 3, background: '#fff', textAlign: 'left', justifyContent: 'flex-start',
                  border: focusedCourse?.id === item.course.id ? `2px solid ${itemAccent}` : BORDER,
                }}
              >
                <Box component="img" src={item.course.avatar} alt="" sx={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{item.course.name}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progress.percent}
                    sx={{ mt: 1, mb: 0.5, height: 5, borderRadius: 3, backgroundColor: '#E8ECF1', '& .MuiLinearProgress-bar': { backgroundColor: itemAccent } }}
                  />
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {progress.total ? `${progress.percent}% completado` : 'Sin clases todavía'}
                  </Typography>
                </Box>
                <Typography sx={{ color: itemAccent, fontWeight: 700, fontSize: 14, px: 1 }}>Entrar</Typography>
              </ButtonBase>
            );
          })}
        </Box>

        {hideNavbar ? (
          // La barra negra mostraba el registro en la CMF; sin ella, va al pie del Inicio
          <Box className="flex flex-col items-center gap-8" sx={{ mt: 6, color: 'text.secondary', fontSize: 12 }}>
            <span>Registrados y Regulados por</span>
            <img src={`${process.env.PUBLIC_URL}/assets/images/logo-CMF-full.png`} alt="CMF" style={{ height: 40, filter: 'invert(1)' }} />
          </Box>
        ) : null}
      </>
    );
  })();

  return (
    <Page
      content={
        <Box sx={{ width: '100%', maxWidth: 1240, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 4 } }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 22, md: 30 }, letterSpacing: '-0.015em' }}>
            Hola{firstName ? `, ${firstName}` : ''}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: { xs: 15, md: 16 }, mb: { xs: 2.5, md: 3 } }}>
            {items ? subtitle : ' '}
            {focusedCourse ? (
              <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: 14 }}>
                Mostrando <b>{focusedCourse.name}</b>. Cambia de curso en el selector de arriba.
              </Box>
            ) : null}
          </Typography>
          {content}
        </Box>
      }
      scroll="normal"
    />
  );
};

export default Home;
